import { whatsappContacts } from '../data/contact';

export const resumeLabels = {
  summary: 'PROFESSIONAL SUMMARY',
  coreCompetencies: 'CORE COMPETENCIES',
  experience: 'PROFESSIONAL EXPERIENCE',
  projects: 'SELECTED PROJECTS',
  achievements: 'ACHIEVEMENTS',
  certifications: 'CERTIFICATIONS & TRAINING',
  education: 'EDUCATION',
  personalDetails: 'PERSONAL DETAILS',
  languages: 'LANGUAGES',
};

export const resumeProfile = {
  name: 'Maher Malhem',
  title: 'Senior AI Engineer | RAG & LLM Systems | Digital Government (DGA Qiyas)',
  email: 'maher.malhem2@gmail.com',
  linkedIn: 'linkedin.com/in/mahermalhem',
  githubUrl: 'https://github.com/mahermalhem',
  dockerHubUrl: 'https://hub.docker.com/u/mahermalhem',
  websiteUrl: 'https://mahermalhem.site',
  portfolioUrl: 'https://mahermalhem.site/projects',
  summary:
    'Senior AI Engineer with 7+ years in software engineering, specializing in private on-premise RAG and LLM platforms (Qwen, GPU inference, PostgreSQL vector search) for University of Bisha while supporting DGA Qiyas digital-transformation evidence for emerging technologies and external beneficiary services. Career progression from Java and backend engineering through senior mobile delivery to AI platform ownership, with enterprise banking consulting (Deloitte / Alinma Bank, KSA), marketplace scale (OpenSooq), and Co-founder & CTO leadership at WASLK. Builds production AI end-to-end on university-owned infrastructure without external AI APIs — PDPL-conscious architecture with data sovereignty and privacy considerations.',
};

export const resumeSaudiPhone =
  whatsappContacts.find((c) => c.id === 'saudi')?.display ?? '+966 53 806 9132';

export const resumePersonalDetails = {
  nationality: 'Jordanian',
  /** Leave empty until verified; omitted from PDF when blank. */
  iqamaStatus: 'transferable',
  noticePeriod: '',
  location: 'Riyadh, Saudi Arabia',
};

export const resumeEducation = {
  degree: 'Software Engineering',
  university: 'Hashemite University, Jordan',
  startYear: '2016',
  graduationYear: '2021',
};

export const resumeCertifications: { name: string; detail?: string }[] = [];

export type ResumeExperienceRolePhase = {
  role: string;
  dateRange: string;
  highlights: string[];
};

export type ResumeExperienceEntry = {
  id: string;
  employerLine: string;
  /** Same client/employer, multiple roles (e.g. promotion). */
  rolePhases?: ResumeExperienceRolePhase[];
  role: string;
  dateRange: string;
  highlights: string[];
};

export const resumeExperiences: ResumeExperienceEntry[] = [
  {
    id: 'rmg',
    role: '',
    dateRange: '',
    highlights: [],
    employerLine: 'RMG, Riyadh, Saudi Arabia | Client: University of Bisha',
    rolePhases: [
      {
        role: 'Senior AI Engineer',
        dateRange: '03/2026 – Present',
        highlights: [
          'Built on-premise private AI on university servers (NVIDIA RTX 6000 Blackwell, Docker, Linux): Qwen LLM inference, Qwen embeddings, RAG — ingestion, chunking, PostgreSQL vector search, retrieval, generation/analysis/evaluation — no external AI APIs',
          'Delivered NestJS backend, React admin dashboard, n8n workflows, Docker setup, RAG integration, and internal LLM deployment for production operations',
          'PDPL-conscious, data-sovereignty architecture — inference and document processing stay on-premise for privacy-sensitive workloads',
          'Supported DGA Qiyas assessment: emerging-technology capabilities for requirement 5.18.4 (Emerging Technologies) and external beneficiary service; evidence contributing to 100% on the applicable requirement',
          'Led delivery with Jira and GitLab — user stories, acceptance criteria, merge-request reviews, structured Qiyas evidence for assessors',
        ],
      },
      {
        role: 'Senior Mobile Developer',
        dateRange: '08/2024 – 03/2026',
        highlights: [
          'Built MyUB React Native for University of Bisha employees — geofenced time punch so staff clock in/out from the campus building they are in, across multiple buildings',
          'Shipped production iOS/Android campus services integrated with university backend systems',
        ],
      },
    ],
  },
  {
    id: 'joacademy',
    role: 'Senior Mobile Developer',
    employerLine: 'JoAcademy | Jordan',
    dateRange: '02/2024 – 08/2024',
    highlights: [
      'Senior React Native developer on a national education platform (student apps, protected premium content); guided 2 junior developers on efficient code, UI quality, and review standards',
      'Owned mobile security: SSL pinning and APK hardening to reduce reverse-engineering exposure; content protection for premium educational assets',
      'Production iOS/Android releases with Firebase integration and performance optimization with backend teams',
    ],
  },
  {
    id: 'digitalcash',
    role: 'React Native Developer',
    employerLine: 'Digital Cash | Jordan',
    dateRange: '02/2023 – 02/2024',
    highlights: [
      'Built React Native salon discovery, booking, and appointment management for production business users',
      'Delivered sales-provider mobile workflows and REST API integrations for field and salon operations',
    ],
  },
  {
    id: 'deloitte',
    role: 'Mobile Apps Consultant',
    employerLine: 'Deloitte | Client: Alinma Bank, Saudi Arabia',
    dateRange: '04/2022 – 01/2023',
    highlights: [
      'Alinma Bank retail banking mobile app — 12-person team; directly led 3 developers (mentoring, code review, delivery), reporting to Tech Team Lead',
      'Enterprise mobile architecture: authentication, compliance-sensitive features, modular design, and production-ready releases',
      'Cross-functional banking collaboration on release quality, performance, and production incident readiness',
    ],
  },
  {
    id: 'opensooq',
    role: 'PHP / Yii2 Backend Developer',
    employerLine: 'OpenSooq | Jordan',
    dateRange: '10/2021 – 03/2022',
    highlights: [
      'Developed Yii2/PHP REST APIs and backend services for internal sales dashboards and high-traffic marketplace operations',
      'Built admin/back-office tooling, MySQL-backed business logic, and reporting for sales and operations teams',
    ],
  },
  {
    id: 'beecell',
    role: 'Java Engineer → React Native Developer',
    employerLine: 'BEECell | Jordan',
    dateRange: '12/2019 – 10/2021',
    highlights: [
      'Progressed from Java engineering to React Native development on Soalk social Q&A application (video, audio, image, text)',
      'Implemented social engagement features (follow, like, share) for multi-format content on iOS/Android',
    ],
  },
];

export type ResumeCompetencyBlock = {
  label: string;
  items: string[];
};

/** Short ATS-friendly blocks — keep each line scannable on ~2 pages. */
export const resumeCoreCompetencyBlocks: ResumeCompetencyBlock[] = [
  {
    label: 'AI / LLM',
    items: [
      'Qwen · LLMs · RAG · embeddings · vector search · LLM inference · RAG evaluation · prompt engineering',
    ],
  },
  {
    label: 'AI infrastructure',
    items: [
      'On-premise AI · GPU inference · Docker · Ollama · vLLM · Linux · n8n · PostgreSQL',
    ],
  },
  {
    label: 'Software engineering',
    items: [
      'NestJS · Node.js · React · React Native · TypeScript · Python · PHP/Yii2 · REST APIs · MySQL · Git · CI/CD',
    ],
  },
  {
    label: 'Leadership & delivery',
    items: [
      'Team leadership · mentoring · code review · sprint planning · Jira · GitLab · user stories · CTO/product (WASLK)',
    ],
  },
  {
    label: 'DGA Qiyas',
    items: [
      'Emerging Technologies (5.18.4) · external beneficiary service · audit-ready evidence · 100% on applicable requirement',
    ],
  },
];

export function getResumeCoreCompetencyBlocks(): ResumeCompetencyBlock[] {
  return resumeCoreCompetencyBlocks;
}

export const resumeSelectedProjects = [
  {
    title: 'Private AI Platform — University of Bisha',
    line:
      'On-premise Qwen RAG (embeddings, PostgreSQL vectors) on RTX 6000 Blackwell; NestJS, React, n8n, Docker; no external AI APIs; DGA Qiyas 5.18.4 / external beneficiary service.',
  },
  {
    title: 'WASLK — B2B Marketplace',
    line: 'NestJS, React, React Native, MySQL (DigitalOcean); wholesale marketplace; 50,000+ JOD revenue in 5 months.',
  },
  {
    title: 'Alinma Bank Mobile — Deloitte',
    line: '12-person team; led 3 developers under Tech Team Lead; enterprise banking mobile (KSA).',
  },
];

export type ResumeAchievementEntry = {
  id: string;
  name?: string;
  description?: string;
  badge?: string;
  /** Venture / leadership highlight with full bullets (also listed under Experience). */
  role?: string;
  employerLine?: string;
  dateRange?: string;
  highlights?: string[];
};

export const resumeAchievements: ResumeAchievementEntry[] = [
  {
    id: 'waslk-venture',
    role: 'Co-founder & CTO',
    employerLine: 'WASLK — B2B wholesale marketplace | Jordan',
    dateRange: '03/2026 – Present',
    highlights: [
      'Co-founded and operate a B2B wholesale marketplace connecting suppliers/warehouses with grocery stores — product, technical roadmap, and release strategy as CTO while maintaining full-time employment',
      'Built full-stack platform: NestJS backend, React admin dashboard, React Native (iOS/Android), MySQL on DigitalOcean',
      'Architected marketplace and order flows with NestJS REST APIs for multi-vendor wholesale operations',
      'Generated 50,000+ JOD revenue within 5 months of live B2B operations',
    ],
  },
  {
    id: 'govjam-2024',
    name: 'GovJam 2024',
    description:
      'Participant / Developer — design and innovation event focused on improving government services and citizen experience.',
  },
  {
    id: 'prince-hussein-award',
    name: 'Prince Hussein Award — Government Apps Competition (2019)',
    description:
      'Android Developer on Business Pocker (team of 2) — Top 5 in the first edition of the best government app competition.',
    badge: 'Top 5',
  },
];

export const resumeLanguages =
  'Arabic: Native | English: Professional Working Proficiency';

/** Strip scheme for compact PDF header lines. */
export function resumeUrlForDisplay(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

export function getResumeHeaderPersonalLine(): string {
  const parts = [
    resumePersonalDetails.location,
    `Nationality: ${resumePersonalDetails.nationality}`,
    resumePersonalDetails.iqamaStatus.trim()
      ? `Iqama: ${resumePersonalDetails.iqamaStatus.trim()}`
      : null,
    resumePersonalDetails.noticePeriod.trim()
      ? `Notice Period: ${resumePersonalDetails.noticePeriod.trim()}`
      : null,
  ].filter(Boolean);
  return parts.join(' | ');
}
